import type { Result, AppError } from '../../error/types';
import { ok } from '../../error/functions';
import { tryCatch } from '../../error/functions/tryCatch';

/**
 * @group Functions
 * @summary <blockquote>Detects the current JavaScript environment (Node.js, Browser, or React Native).</blockquote>
 * 
 * @returns Result containing the detected environment or an error
 * 
 * @example
 * ```typescript
 * import { getJavascriptEnvironment } from '@shared/utils';
 * 
 * const result = getJavascriptEnvironment();
 * result.match({
 *   ok: (env) => {
 *     if (env === 'react-native') {
 *       // React Native specific code
 *     }
 *   },
 *   err: (error) => console.error(`Detection failed: ${error.errorMessage}`)
 * });
 * ```
 */
export function getJavascriptEnvironment(): Result<'node' | 'browser' | 'react-native', AppError> {
  return tryCatch(() => {
    // Check for native React Native (has global but no window, plus RN-specific globals)
    // Production mobile builds only include native React Native (iOS/Android) - React Native Web is dev-only
    if (typeof window === 'undefined' && typeof (global as any) !== 'undefined') {
      // Check for React Native specific globals
      if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        return 'react-native' as const;
      }
      
      // Check for Expo specific globals
      if ((global as any).__expo) {
        return 'react-native' as const;
      }
      
      // Check for other React Native indicators
      if ((global as any).HermesInternal || (global as any).__fbBatchedBridge) {
        return 'react-native' as const;
      }
      
      // If it has global but no window and no RN indicators, it's likely Node.js
      return 'node' as const;
    }
    
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      return 'browser' as const;
    }
    
    // Fallback - assume Node.js if neither window nor global is available
    // This covers edge cases like certain serverless environments
    return 'node' as const;
  }).mapErr((error) => ({
    errorCode: 'JAVASCRIPT_ENVIRONMENT_DETECTION_FAILED',
    errorMessage: `Failed to detect JavaScript environment: ${error instanceof Error ? error.message : 'Unknown error'}`
  }));
}
